import { useEffect, useState } from 'react';
import { Control, Controller, FieldValues, Path, useController } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import BrandedChip from '../BrandedChip';
import Text from '../Text';

export type QuestionType = {
  id: string;
  text: string;
  order: number | null;
  score: string;
  answer?: string | null;
};

export type QuestionTypeProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  questionOptions: QuestionType[];
  groupLabel?: React.ReactNode;
  showError?: boolean;
};

const QuestionGroup = <T extends FieldValues>({
  name,
  control,
  questionOptions,
  groupLabel,
  showError = true
}: QuestionTypeProps<T>) => {
  const {
    field: { value },
    fieldState: { isTouched }
  } = useController({ name, control });

  const answersOptions = ['Yes', 'No'];

  const [questionValues, setQuestionValues] = useState<{ id: string; answer: string | null }[]>(value);

  const handleSelect = (questionId: string, answer: string | null) => {
    const updatedQuestions = questionValues.map((question) =>
      question.id === questionId ? { ...question, answer: answer === question.answer ? null : answer } : question
    );

    setQuestionValues(updatedQuestions);
    return updatedQuestions;
  };

  useEffect(() => {
    if (!isTouched) {
      if (value) {
        setQuestionValues(value);
      } else if (questionOptions?.length) {
        setQuestionValues(questionOptions.map((option) => ({ id: option.id, answer: null })));
      }
    }
  }, [isTouched, questionOptions, value]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange }, fieldState: { error } }) => (
        <View>
          {groupLabel && (
            <Text fontWeight="700" style={styles.groupLabel}>
              {groupLabel}
            </Text>
          )}

          <View style={styles.container}>
            {questionOptions.map((questionOption) => (
              <View key={questionOption.id} style={styles.questionContainer}>
                <Text fontWeight="500" style={styles.questionText}>
                  {questionOption.text}
                </Text>

                <View style={styles.answersContainer}>
                  <BrandedChip
                    key={answersOptions[0]}
                    isSelected={questionValues?.find((it) => it.id === questionOption.id)?.answer === answersOptions[0]}
                    value={answersOptions[0]}
                    onPress={() => onChange(handleSelect(questionOption.id, answersOptions[0]))}
                  />
                  <BrandedChip
                    key={answersOptions[1]}
                    isSelected={questionValues?.find((it) => it.id === questionOption.id)?.answer === answersOptions[1]}
                    value={answersOptions[1]}
                    onPress={() => onChange(handleSelect(questionOption.id, answersOptions[1]))}
                  />
                </View>
              </View>
            ))}
          </View>
          {showError && !!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
        </View>
      )}
    />
  );
};

export default QuestionGroup;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    gap: 10,
    justifyContent: 'center',
    paddingBottom: 20
  },

  groupLabel: {
    marginBottom: 10,
    color: 'black'
  },

  questionContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3.5
  },

  questionText: {
    flex: 1,
    fontSize: 16
  },

  answersContainer: {
    width: 125,
    flexDirection: 'row',
    gap: 1.5
  },

  errorText: {
    marginTop: 10,
    fontSize: 12,
    color: 'red'
  }
});
